<script setup lang="ts">
import GoogleMap from '@components/GoogleMap.vue';
import { useSearchMapStore } from '@stores/useSearchMapStore';
import { storeToRefs } from 'pinia';
import Marker from '@components/Marker.vue';
import { CustomMarker } from 'vue3-google-map';
import Icon from '@components/Icon.vue';
import { IconNavigationArrowFill, IconX } from '@icons';
import { useSearchStore } from '@stores/useSearchStore.ts';
import ZoomMapButtons from '@components/StickyTabs/ZoomMapButtons.vue';
import SearchMapButton from '@pages/search/components/map/SearchMapButton.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import UserLocationMarker from '@components/UserLocationMarker.vue';
import { computed } from 'vue';
import SearchMapCard from '@pages/search/components/map/SearchMapCard.vue';
import { type GoogleMapLatLngLiteral, MapMarkerType, type SearchMapMarker } from '@global-types/googleMap.ts';
import type { ExtendedAlgoliaOffer } from '@global-types/algoliaOffer.ts';
import Typography from '@components/Typography.vue';

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

const searchStore = useSearchStore();
const { closeMap, setHoverOfferHashId } = searchStore;
const { hoverOfferHashId, offers } = storeToRefs(searchStore);

const searchMapStore = useSearchMapStore();
const {
  initMap,
  zoomIn,
  zoomOut,
  geolocate,
  isMarkerClicked,
  triggerOfferPanel,
  closeOfferPanel,
} = searchMapStore;

const {
  center,
  customMarkerAnchorPoint,
  primaryMarkers,
  secondaryMarkers,
  userLocation,
  selectedOfferHashId,
} = storeToRefs(searchMapStore);

const markers = computed<SearchMapMarker[]>(() => [ ...primaryMarkers.value, ...secondaryMarkers.value ]);

const selectedOffer = computed<ExtendedAlgoliaOffer | undefined>(() => offers.value.find(
  (offer) => offer.hash_id === selectedOfferHashId.value,
));

const selectedOfferPosition = computed<GoogleMapLatLngLiteral | null>(() => {
  const marker = markers.value.find((item) => item.id === selectedOfferHashId.value);
  if (marker) {
    return {
      lat: marker.lat,
      lng: marker.lng,
    };
  }

  if (!selectedOffer.value?._geoloc) {
    return null;
  }

  return {
    lat: selectedOffer.value._geoloc.lat,
    lng: selectedOffer.value._geoloc.lng,
  };
});

const isHighlighted = (markerId: string): boolean => markerId === hoverOfferHashId.value || markerId === selectedOfferHashId.value;

const getMarkerClass = (markerId: string): string => {
  const isClickedClass = isMarkerClicked(markerId) && !isHighlighted(markerId) ? 'text-gray-darker' : '';

  return `flex items-center gap-x-2 !p-0 ${isClickedClass}`.trim();
};
</script>

<template>
  <div class="relative">
    <div
      v-if="!isMobileOrTablet"
      class="search-map__header left-2"
    >
      <SearchMapButton @click="closeMap">
        <Icon
          :svg="IconX"
          width="24"
          height="24"
          class="text-black"
        />
      </SearchMapButton>
    </div>

    <div class="search-map__header right-2">
      <ZoomMapButtons
        class="!hidden lg:!flex"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
      />

      <SearchMapButton @click="geolocate">
        <Icon
          :svg="IconNavigationArrowFill"
          width="24"
          height="24"
          class="text-black"
        />
      </SearchMapButton>
    </div>

    <GoogleMap
      :center="center"
      height="100%"
      :is-static="false"
      :has-fullscreen-control="false"
      has-camera-control
      :map-styles="[
        {
          featureType: 'poi',
          stylers: [ { visibility: 'off' } ],
        },
      ]"
      @ready="initMap"
    >
      <Marker
        v-for="(marker, index) in markers"
        :key="marker.id"
        :marker-class="getMarkerClass(marker.id as string)"
        :type="marker.type"
        :position="{
          lat: marker.lat,
          lng: marker.lng,
        }"
        :z-index="marker.type === MapMarkerType.PRIMARY ? index + 1 : 1"
        :is-highlighted="isHighlighted(marker.id as string)"
        @click.stop="() => triggerOfferPanel(marker.id as string)"
        @dblclick.stop
        @mouseenter="() => setHoverOfferHashId(marker.id as string)"
        @mouseleave="() => setHoverOfferHashId(null)"
      >
        <template v-if="marker.groupedMarkerTitles?.length">
          <div class="flex items-stretch">
            <span class="self-center px-2 group-hover:hidden">
              {{ marker.title }}
            </span>

            <ul
              :class="[
                'm-0',
                'hidden flex-col group-hover:flex',
                'px-2 py-1',
              ]"
            >
              <template v-if="marker.groupedMarkerTitles.length + 1 <= 5">
                <li
                  v-for="groupedMarker in [ marker.title, ...marker.groupedMarkerTitles ]"
                  :key="groupedMarker"
                  class="flex"
                >
                  {{ groupedMarker }}
                </li>
              </template>

              <template v-else>
                <li
                  v-for="groupedMarker in [ marker.title, ...marker.groupedMarkerTitles ].slice(0, 4)"
                  :key="groupedMarker"
                  class="flex"
                >
                  {{ groupedMarker }}
                </li>

                <li class="flex">
                  ...
                </li>

                <li class="flex">
                  {{ marker.groupedMarkerTitles[marker.groupedMarkerTitles.length - 1] }}
                </li>
              </template>
            </ul>

            <Typography
              variant="body-small-bold"
              color="gray-dark"
              class="flex items-center justify-center bg-gray px-2 py-1"
            >
              {{ marker.groupedMarkerTitles.length + 1 }}
            </Typography>
          </div>
        </template>

        <template v-else>
          <span class="px-2 py-1">
            {{ marker.title }}
          </span>
        </template>
      </Marker>

      <CustomMarker
        v-if="selectedOffer && selectedOfferPosition && !isMobileOrTablet"
        :key="selectedOffer.hash_id"
        :class="customMarkerAnchorPoint.startsWith('TOP_') ? '-mt-3' : 'mt-12'"
        :options="{
          position: selectedOfferPosition,
          anchorPoint: customMarkerAnchorPoint,
          zIndex: 210,
        }"
        @click.stop
      >
        <SearchMapCard
          :key="selectedOffer.hash_id"
          :offer="selectedOffer"
          @close="closeOfferPanel"
        />
      </CustomMarker>

      <UserLocationMarker
        v-if="userLocation"
        :location="userLocation"
      />
    </GoogleMap>

    <div
      v-if="selectedOffer && isMobileOrTablet"
      :class="[
        'absolute bottom-28 left-1/2 z-10',
        '-translate-x-1/2',
      ]"
    >
      <SearchMapCard
        :key="selectedOffer.hash_id"
        :offer="selectedOffer"
        @close="closeOfferPanel"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.search-map {
  &__header {
    @apply absolute top-[72px] z-10 lg:top-2;

    @apply flex flex-col gap-y-2;
  }
}
</style>
